import { Link, useNavigate } from "react-router-dom";
import { Copy, ExternalLink, Images, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@groot/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@groot/ui/card";
import { StatusBadge } from "@groot/ui/status-badge";
import { Text } from "@groot/ui/typography";
import type { PhotoRequest } from "@/pages/photo-request/hooks/usePhotoRequests";
import { previewStatusLabel } from "../PrintBatches";
import {
  previewUrl,
  useCreatePrintBatch,
  usePrintBatches,
  whatsappUrl,
} from "../hooks/usePrintBatches";

/** Print sheets and the customer preview for one photo request. */
export function PrintSheetsCard({ request }: { request: PhotoRequest }) {
  const navigate = useNavigate();
  const { data: batches } = usePrintBatches(request.id);
  const create = useCreatePrintBatch();
  const hasPhotos = (request.files?.length ?? 0) > 0 && !request.photosClearedAt;
  const link =
    request.previewLink && request.previewSharedAt ? previewUrl(request.previewLink) : null;

  const createBatch = () =>
    create.mutate(
      { photoRequestIds: [request.id] },
      {
        onSuccess: (batch) => navigate(`/print-batches/${batch.id}`),
        onError: (error) =>
          toast.error("Could not create print sheets", { description: error.message }),
      },
    );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle>Print sheets</CardTitle>
        <Button size="sm" onClick={createBatch} disabled={!hasPhotos || create.isPending}>
          {create.isPending ? <Loader2 className="animate-spin" /> : <Images />}
          {create.isPending ? "Laying out…" : "Create print sheets"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {!batches?.length ? (
          <Text variant="meta" className="block">
            {hasPhotos
              ? "Lay every photo out on A4 polaroid sheets at the ordered sizes, then fine-tune and download a print-ready PDF."
              : "Print sheets can be made once the customer has uploaded photos."}
          </Text>
        ) : (
          <ul className="divide-y divide-border">
            {batches.map((batch) => {
              const source = batch.sources.find((s) => s?.photoRequestId === request.id) ?? null;
              return (
                <li key={batch.id} className="flex flex-wrap items-center gap-2 py-2">
                  <Link
                    to={`/print-batches/${batch.id}`}
                    className="min-w-0 flex-1 truncate text-sm font-medium hover:text-primary"
                  >
                    {batch.name}
                  </Link>
                  <Text variant="meta">{previewStatusLabel(source)}</Text>
                  <StatusBadge
                    status={
                      batch.status === "printed"
                        ? "success"
                        : batch.status === "draft"
                          ? "pending"
                          : "cancelled"
                    }
                    label={batch.status.replace("_", " ")}
                  />
                </li>
              );
            })}
          </ul>
        )}
        {link && (
          <div className="space-y-2 rounded-xl bg-muted p-3">
            <Text variant="body" className="block font-medium">
              Customer preview
            </Text>
            <Text variant="meta" className="block truncate">
              {link}
            </Text>
            <div className="flex flex-wrap gap-1">
              <Button asChild size="sm" variant="outline">
                <a
                  href={whatsappUrl(
                    request.customer?.mobileNumber ?? null,
                    request.customer?.name ?? "there",
                    link,
                  )}
                  target="_blank"
                  rel="noreferrer"
                >
                  <MessageCircle />
                  WhatsApp
                </a>
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  void navigator.clipboard.writeText(link);
                  toast.success("Preview link copied");
                }}
              >
                <Copy />
                Copy
              </Button>
              <Button asChild size="sm" variant="ghost">
                <a href={link} target="_blank" rel="noreferrer">
                  <ExternalLink />
                  Open
                </a>
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
